<script lang="ts">
	import {
		Button,
		ButtonSize,
		ButtonType,
		Icon,
		PageHeader,
		ReferenceTableRowModal,
		rowActions
	} from '@repo/commons/components'
	import type { ReferenceTableRow, TableData } from '@repo/commons/models'
	import {
		confirmDelete,
		injectTranslation,
		showErrorToast,
		showSaveFailedToast
	} from '@repo/commons/utils'

	import { t } from 'svelte-i18n'
	import ReferenceTableCsvActions from './ReferenceTableCsvActions.svelte'
	import ReferenceTableRowsTable from './ReferenceTableRowsTable.svelte'
	import type { ResourceSchema } from '@semanticedge/persistence-service-noheaders-client/src'

	type Props = {
		title: string
		rows: readonly ReferenceTableRow[]
		schema: ResourceSchema | undefined
		onSaveRows: (rows: ReferenceTableRow[]) => Promise<boolean>
	}

	const { title, rows, schema, onSaveRows }: Props = $props()

	let rowModalVisible = $state(false)
	let editingRowIndex = $state<number | null>(null)
	let prefilledRow = $state<ReferenceTableRow | null>(null)
	let searchQuery = $state('')
	let selectedRows = $state<TableData[]>([])

	const editingRow = $derived(
		editingRowIndex !== null ? (rows[editingRowIndex] ?? null) : prefilledRow
	)

	const hasSelection = $derived(selectedRows.length > 0)

	const deleteSelectedLabel = $derived(
		injectTranslation('ReferenceTableRowsView.deleteSelected', {
			count: String(selectedRows.length)
		})
	)

	const persistRows = (nextRows: ReferenceTableRow[]): Promise<boolean> => {
		selectedRows = []

		return onSaveRows(nextRows)
	}

	const persistRowsOrNotify = async (nextRows: ReferenceTableRow[]): Promise<void> => {
		if (!(await persistRows(nextRows))) showSaveFailedToast()
	}

	const openRowModal = (rowIndex: number | null, prefill: ReferenceTableRow | null): void => {
		editingRowIndex = rowIndex
		prefilledRow = prefill
		rowModalVisible = true
	}

	const openAddRowModal = (): void => {
		if (!schema) {
			showErrorToast($t('ResourceSchemaColumnsView.missingSchema'))

			return
		}

		openRowModal(null, null)
	}

	const editRow = (rowIndex: number): void => {
		openRowModal(rowIndex, null)
	}

	const duplicateRow = (rowIndex: number): void => {
		const sourceRow = rows[rowIndex]
		if (!sourceRow || !schema) return

		openRowModal(null, { ...sourceRow })
	}

	const closeRowModal = (): void => {
		rowModalVisible = false
		editingRowIndex = null
		prefilledRow = null
	}

	const handleSaveRow = (row: ReferenceTableRow): Promise<void> => {
		const nextRows =
			editingRowIndex === null
				? [...rows, row]
				: rows.map((existingRow, index) => (index === editingRowIndex ? row : existingRow))

		return persistRowsOrNotify(nextRows)
	}

	const deleteRow = async (rowIndex: number): Promise<void> => {
		const entity = $t('Entity.reference_table_row')
		const confirmed = await confirmDelete(
			injectTranslation('Confirmation.delete_title', { entity }),
			injectTranslation('Confirmation.delete_message', { entity })
		)

		if (!confirmed) return

		await persistRowsOrNotify(rows.filter((_, index) => index !== rowIndex))
	}

	const deleteSelectedRows = async (): Promise<void> => {
		const confirmed = await confirmDelete(
			$t('ReferenceTableRowsView.deleteSelectedTitle'),
			injectTranslation('ReferenceTableRowsView.deleteSelectedMessage', {
				count: String(selectedRows.length)
			})
		)

		if (!confirmed) return

		const selectedIndexes = new Set(selectedRows.map(({ id }) => Number(id)))

		await persistRowsOrNotify(rows.filter((_, index) => !selectedIndexes.has(index)))
	}

	const ROW_ACTION_HANDLER: Partial<Record<rowActions, (rowIndex: number) => void>> = {
		[rowActions.EDIT]: editRow,
		[rowActions.DUPLICATE]: duplicateRow,
		[rowActions.DELETE]: deleteRow
	}

	const handleRowAction = (action: rowActions, rowIndex: number): void => {
		ROW_ACTION_HANDLER[action]?.(rowIndex)
	}

	const handleSearch = (query: string): void => {
		searchQuery = query
		selectedRows = []
	}

	const handleSelectionChange = (selection: TableData[]): void => {
		selectedRows = selection
	}
</script>

<div class="content-wrapper">
	<PageHeader
		title={title || $t('ReferenceTableRowsView.title')}
		onAdd={openAddRowModal}
		onSearch={handleSearch}
	>
		{#snippet rightSnippet()}
			{#if hasSelection}
				<Button
					buttonType={ButtonType.DESTRUCTIVE_OUTLINE}
					buttonSize={ButtonSize.LARGE}
					buttonText={deleteSelectedLabel}
					iconProps={{ name: 'trash', size: 'sm' }}
					onclick={deleteSelectedRows}
				/>
			{/if}
			<ReferenceTableCsvActions {title} {rows} {schema} onImport={persistRows} />
		{/snippet}
	</PageHeader>

	<ReferenceTableRowsTable
		{rows}
		{schema}
		{searchQuery}
		{selectedRows}
		onSelectionChange={handleSelectionChange}
		onRowAction={handleRowAction}
	/>
</div>

<ReferenceTableRowModal
	visible={rowModalVisible}
	{schema}
	existingRow={editingRow}
	editingIndex={editingRowIndex}
	isDuplicate={prefilledRow !== null}
	existingRows={rows}
	onSave={handleSaveRow}
	onClose={closeRowModal}
/>

<style lang="postcss">
	.content-wrapper {
		@apply flex flex-col gap-4 min-w-0;
	}
</style>
